import { ModalVisibilityProps } from 'sb-modal'
import { useCallback, useMemo, useQuery, useRef, useState, useEffect } from 'sb-hooks'
import request from 'sb-request'
import { snowplow } from 'common/helpers'

import { openReviewThankYouModal } from 'compositions/modals/review/ReviewThankYouModal/ReviewThankYouModal'

import productTagsQuery from './productTags.graphql'
import rateTagQuery from './rateTag.graphql'


const useReviewTagsModal = (props: Review.ReviewTagsModalProps & Pick<ModalVisibilityProps, 'closeModal'>) => {
  const [ step, setStep ] = useState<number>(1) // from 1

  const { productId, reviewId, rating, source, closeModal, onTagsSaved } = props

  useEffect(() => {
    snowplow.track('Review extended popup', {
      source,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // fetch tags
  const productsTagsQueryParams = useMemo(() => ({
    name: 'productTags',
    variables: {
      productId,
    },
    modifyResult: (data) => data.product.tagTypes,
  }), [ productId ])

  const { data: groups, isFetching } = useQuery<Review.TagGroups>(productTagsQuery, productsTagsQueryParams)

  const stepCount = groups ? groups.length : 0

  const handleFinish = useCallback(() => {
    closeModal()

    openReviewThankYouModal({ productId, rating })
  }, [ closeModal, productId, rating ])

  const handleSkip = useCallback(() => {
    // TODO add analytics - added on 2020-04-21 by maddoger
    handleFinish()
  }, [ handleFinish ])


  // votes
  const [ isSubmitting, setSubmitting ] = useState<boolean>(false)

  // voted tags cache
  const votedTags = useRef<number[]>([])

  const handleVote = useCallback(async (tagId: number) => {
    votedTags.current.push(tagId)

    if (step === stepCount) {
      setSubmitting(true)

      try {
        await request.gql<{ input: { reviewId: number, tagIds: number[] } }, any>('rateTag', {
          body: {
            query: rateTagQuery,
            variables: {
              input: {
                reviewId,
                tagIds: votedTags.current,
              },
            },
          },
          withNestedErrors: true,
        })

        if (typeof onTagsSaved === 'function') {
          onTagsSaved()
        }

        handleFinish()
      }
      catch (error) {
        console.log(error)

        setSubmitting(false)
      }

      return
    }

    setStep(step + 1)
  }, [ step, stepCount, reviewId, onTagsSaved, handleFinish ])

  const currentGroup = groups ? groups[step - 1] : null

  return {
    isFetching: isFetching || isSubmitting,
    step,
    stepCount,
    type: currentGroup ? currentGroup.type : null,
    tags: currentGroup ? currentGroup.tags : [],
    handleSkip,
    handleVote,
  }
}


export default useReviewTagsModal
